import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  getDocs,
  where,
  documentId,
  updateDoc,
  doc,
  serverTimestamp,
  addDoc, // ✅ NOVO
} from "firebase/firestore";

import { db } from "../firebase/firebase";

// Layout
import BaseLayout from "../components/layout/BaseLayout";

// CSS
import "./AstroMapLeads.css";

const ESSENTIAL_KEYS = [
  "dataNascimento",
  "cidadeNascimento",
  "horaNascimento",
  "focoMapa",
  "periodoLeitura",
  "statusRelacionamento",
  "objetivo2026",
  "desafios",
  "tipoPessoaAtrai",
  "limites",
  "medos",
];

const ALL_KEYS_FOR_PROGRESS = [
  "nome",
  "email",
  "telefone",
  "dataNascimento",
  "horaNascimento",
  "cidadeNascimento",
  "estadoNascimento",
  "paisNascimento",
  "genero",
  "orientacao",
  "focoMapa",
  "experienciaAstro",
  "periodoLeitura",
  "statusRelacionamento",
  "objetivo2026",
  "desafios",
  "tipoPessoaAtrai",
  "limites",
  "medos",
  "pontosFortes",
  "preferencias",
  "aceitaWhatsApp",
  "aceitaEmail",
];

function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("pt-BR");
  } catch {
    return "—";
  }
}

function safeStr(v) {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function chunkArray(arr, size = 10) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function computeProgress(docData) {
  const answers = docData?.answers || {};
  let filled = 0;
  let total = 0;

  for (const k of ALL_KEYS_FOR_PROGRESS) {
    total++;
    const v = answers[k];

    if (typeof v === "boolean") {
      filled++;
      continue;
    }

    if (typeof v === "string") {
      if (v.trim().length > 0) filled++;
      continue;
    }

    if (v !== null && v !== undefined && String(v).trim() !== "") {
      filled++;
    }
  }

  const percent = total === 0 ? 0 : Math.round((filled / total) * 100);
  return { filled, total, percent };
}

function computeIsComplete(docData) {
  const answers = docData?.answers || {};
  for (const k of ESSENTIAL_KEYS) {
    const v = answers[k];
    if (typeof v === "boolean") continue;
    if (!v || safeStr(v).length === 0) return false;
  }
  return true;
}

function statusLabel(status) {
  const s = safeStr(status || "draft").toLowerCase();
  if (s === "checkout_started") return "Checkout iniciado";
  if (s === "paid") return "Pago";
  if (s === "abandoned") return "Abandonado";
  return "Rascunho";
}

function statusPillClass(status) {
  const s = safeStr(status || "draft").toLowerCase();
  if (s === "checkout_started") return "pill pill-blue";
  if (s === "paid") return "pill pill-green";
  if (s === "abandoned") return "pill pill-gray";
  return "pill pill-pink";
}

// ✅ Mesma lógica do EnviarNotificacao para extras
function buildExtraData(actionType, customDataText) {
  if (!actionType || actionType === "none") {
    return { type: "push", route: "/notifications" };
  }

  if (actionType === "custom") {
    try {
      const obj = JSON.parse(customDataText || "{}");
      return obj && typeof obj === "object"
        ? obj
        : { type: "custom", route: "/notifications" };
    } catch (_) {
      return { type: "custom", route: "/notifications", invalidJson: true };
    }
  }

  return { type: "push", route: "/notifications" };
}

export default function AstroMapLeads() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Cache local de usuários (userId -> {nome, email, telefone})
  const [usersMap, setUsersMap] = useState({});

  const [yearFilter, setYearFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [queryText, setQueryText] = useState("");

  const [selected, setSelected] = useState(null);

  // ✅ Modal de notificação (Fluxo A)
  const [notifyLead, setNotifyLead] = useState(null);

  // ============================
  // 🔥 Realtime drafts
  // ============================
  useEffect(() => {
    setLoading(true);

    const q = query(
      collection(db, "astro_map_drafts"),
      orderBy("updatedAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(list);
        setLoading(false);
      },
      (err) => {
        console.error("Erro ao carregar astro_map_drafts:", err);
        setRows([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  // ============================
  // ✅ Carrega dados da coleção "users" (fallback)
  // ============================
  useEffect(() => {
    const ids = Array.from(
      new Set(
        rows
          .map((r) => safeStr(r.userId))
          .filter((id) => id && id.length > 0)
      )
    );

    if (ids.length === 0) return;

    const missing = ids.filter((id) => !usersMap[id]);
    if (missing.length === 0) return;

    let cancelled = false;

    (async () => {
      try {
        const chunks = chunkArray(missing, 10);
        const nextMap = {};

        for (const group of chunks) {
          const qUsers = query(
            collection(db, "users"),
            where(documentId(), "in", group)
          );

          const snap = await getDocs(qUsers);
          snap.forEach((docSnap) => {
            const data = docSnap.data() || {};
            nextMap[docSnap.id] = {
              nome: safeStr(data.nome) || safeStr(data.name) || "",
              email: safeStr(data.email) || "",
              telefone:
                safeStr(data.telefone) ||
                safeStr(data.whatsapp) ||
                safeStr(data.phone) ||
                "",
            };
          });
        }

        if (!cancelled) {
          setUsersMap((prev) => ({ ...prev, ...nextMap }));
        }
      } catch (e) {
        console.error("Erro ao carregar usuários (users):", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rows, usersMap]);

  // ============================
  // ✅ Atualizações ADMIN no lead (toggle)
  // - adminFlags.notified + adminFlags.notifiedAt
  // - adminFlags.manualPaid + adminFlags.manualPaidAt
  // ============================
  const toggleAdminFlag = async (leadId, flagKey, nextValue, extraPayload = {}) => {
    try {
      const ref = doc(db, "astro_map_drafts", leadId);

      const payload = {
        [`adminFlags.${flagKey}`]: nextValue,
        updatedAt: serverTimestamp(),
        ...extraPayload,
      };

      // timestamps só quando liga
      if (flagKey === "notified") {
        payload["adminFlags.notifiedAt"] = nextValue ? serverTimestamp() : null;
      }
      if (flagKey === "manualPaid") {
        payload["adminFlags.manualPaidAt"] = nextValue ? serverTimestamp() : null;
      }

      await updateDoc(ref, payload);
    } catch (e) {
      console.error("Erro ao atualizar adminFlags:", e);
      alert("Não foi possível atualizar. Veja o console.");
    }
  };

  const yearsAvailable = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const y = r?.year;
      if (y) set.add(String(y));
    });
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [rows]);

  const filtered = useMemo(() => {
    const text = queryText.trim().toLowerCase();

    return rows.filter((r) => {
      const yOk =
        yearFilter === "todos" ? true : String(r.year) === String(yearFilter);

      const st = safeStr(r.status || "draft").toLowerCase();
      const sOk = statusFilter === "todos" ? true : st === statusFilter;

      if (!text) return yOk && sOk;

      const a = r.answers || {};
      const u = usersMap[safeStr(r.userId)] || {};

      const nome = safeStr(a.nome) || safeStr(u.nome);
      const email = safeStr(a.email) || safeStr(u.email);
      const tel = safeStr(a.telefone) || safeStr(u.telefone);

      const hay = [
        nome,
        email,
        tel,
        safeStr(a.cidadeNascimento),
        safeStr(a.estadoNascimento),
        safeStr(a.paisNascimento),
        safeStr(r.userId),
        safeStr(r.id),
      ]
        .join(" ")
        .toLowerCase();

      return yOk && sOk && hay.includes(text);
    });
  }, [rows, yearFilter, statusFilter, queryText, usersMap]);

  const metrics = useMemo(() => {
    let total = rows.length;
    let completos = 0;
    let checkout = 0;
    let paid = 0;

    rows.forEach((r) => {
      if (computeIsComplete(r)) completos++;
      const st = safeStr(r.status || "draft").toLowerCase();
      if (st === "checkout_started") checkout++;
      if (st === "paid") paid++;
    });

    return { total, completos, checkout, paid };
  }, [rows]);

  return (
    <BaseLayout>
      <div className="astro-leads-wrap">
        <div className="astro-leads-header">
          <div className="header-info">
            <h2>Leads — Mapa Astral</h2>
            <p>
              Aqui aparecem <b>todos os rascunhos</b> (mesmo sem pagamento) para
              você futuramente mandar desconto / push / remarketing.
            </p>
          </div>

          <div className="astro-metrics">
            <div className="metric-card">
              <span>Total</span>
              <strong>{metrics.total}</strong>
            </div>
            <div className="metric-card">
              <span>Completos</span>
              <strong>{metrics.completos}</strong>
            </div>
            <div className="metric-card">
              <span>Checkout</span>
              <strong>{metrics.checkout}</strong>
            </div>
            <div className="metric-card">
              <span>Pagos</span>
              <strong>{metrics.paid}</strong>
            </div>
          </div>
        </div>

        <div className="astro-filters">
          <div className="filter-group">
            <label>Ano</label>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <option value="todos">Todos</option>
              {yearsAvailable.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="todos">Todos</option>
              <option value="draft">Rascunho</option>
              <option value="checkout_started">Checkout iniciado</option>
              <option value="paid">Pago</option>
              <option value="abandoned">Abandonado</option>
            </select>
          </div>

          <div className="filter-search">
            <input
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              placeholder="Buscar por nome, e-mail, WhatsApp, cidade ou userId…"
            />
          </div>
        </div>

        <div className="astro-table-card">
          {loading ? (
            <div className="astro-loading">
              <div className="spinner" />
              <span>Carregando leads…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="astro-empty">
              Nenhum lead encontrado com esses filtros.
            </div>
          ) : (
            <div className="astro-table-wrap">
              <table className="astro-table">
                <thead>
                  <tr>
                    <th>Contato</th>
                    <th>Consentimento</th>
                    <th>Preenchimento</th>
                    <th>Status</th>
                    <th>Atualizado</th>
                    <th>Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((r) => {
                    const a = r.answers || {};
                    const u = usersMap[safeStr(r.userId)] || {};

                    const nome = safeStr(a.nome) || safeStr(u.nome) || "—";
                    const email = safeStr(a.email) || safeStr(u.email) || "—";
                    const tel = safeStr(a.telefone) || safeStr(u.telefone) || "—";

                    const allowWpp = a.aceitaWhatsApp === true;
                    const allowEmail = a.aceitaEmail === true;

                    const { percent } = computeProgress(r);
                    const complete = computeIsComplete(r);

                    // ✅ flags admin
                    const adminFlags = r.adminFlags || {};
                    const notified = adminFlags.notified === true;
                    const manualPaid = adminFlags.manualPaid === true;

                    return (
                      <tr key={r.id}>
                        <td>
                          <div className="contact-cell">
                            <div className="contact-name">{nome}</div>

                            <div className="contact-sub">
                              <span className="mono">{email}</span>
                              <span className="dot">•</span>
                              <span className="mono">{tel}</span>
                            </div>

                            <div className="contact-sub muted">
                              userId:{" "}
                              <span className="mono">
                                {safeStr(r.userId) || "—"}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="consents">
                            <span
                              className={
                                allowWpp ? "tag tag-green" : "tag tag-gray"
                              }
                            >
                              WhatsApp {allowWpp ? "OK" : "Não"}
                            </span>
                            <span
                              className={
                                allowEmail ? "tag tag-green" : "tag tag-gray"
                              }
                            >
                              E-mail {allowEmail ? "OK" : "Não"}
                            </span>

                            {notified && (
                              <span className="tag tag-blue">Notificado</span>
                            )}
                            {manualPaid && (
                              <span className="tag tag-green">
                                Pago (manual)
                              </span>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="progress-cell">
                            <div className="progress-top">
                              <span
                                className={
                                  complete ? "tag tag-green" : "tag tag-pink"
                                }
                              >
                                {complete ? "Completo" : "Incompleto"}
                              </span>
                              <span className="progress-percent">{percent}%</span>
                            </div>

                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${percent}%` }}
                              />
                            </div>

                            <div className="progress-sub">
                              Nascimento:{" "}
                              <b>
                                {safeStr(a.dataNascimento) ? "OK" : "Falta"}
                              </b>{" "}
                              <span className="dot">•</span>
                              Cidade:{" "}
                              <b>
                                {safeStr(a.cidadeNascimento) ? "OK" : "Falta"}
                              </b>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className={statusPillClass(r.status)}>
                            {statusLabel(r.status)}
                          </span>
                        </td>

                        <td className="mono">{fmtDate(r.updatedAt)}</td>

                        <td>
                          <div className="actions">
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={() => setSelected(r)}
                            >
                              Ver
                            </button>

                            <button
                              className="btn btn-ghost"
                              type="button"
                              onClick={() => {
                                const txt =
                                  `Nome: ${nome}\n` +
                                  `Email: ${email}\n` +
                                  `WhatsApp: ${tel}\n` +
                                  `Ano: ${r.year || "—"}\n` +
                                  `Status: ${statusLabel(r.status)}\n` +
                                  `userId: ${safeStr(r.userId) || "—"}\n` +
                                  `Notificado: ${notified ? "Sim" : "Não"}\n` +
                                  `Pago manual: ${manualPaid ? "Sim" : "Não"}\n`;
                                navigator.clipboard?.writeText(txt);
                              }}
                            >
                              Copiar
                            </button>

                            {/* ✅ NOVO: abre modal do Fluxo A */}
                            <button
                              className={`btn btn-mini ${
                                notified ? "btn-blue" : "btn-ghost"
                              }`}
                              type="button"
                              title={
                                notified
                                  ? "Enviar novo push para este lead (já notificado)"
                                  : "Enviar push e marcar como notificado"
                              }
                              onClick={() => setNotifyLead(r)}
                            >
                              {notified ? "Notificado ✓" : "Notificar"}
                            </button>

                            {/* ✅ Pago manual continua igual */}
                            <button
                              className={`btn btn-mini ${
                                manualPaid ? "btn-green" : "btn-ghost"
                              }`}
                              type="button"
                              title={
                                manualPaid
                                  ? "Desmarcar pago manual"
                                  : "Marcar como PAGO (manual)"
                              }
                              onClick={() =>
                                toggleAdminFlag(r.id, "manualPaid", !manualPaid)
                              }
                            >
                              {manualPaid ? "Pago ✓" : "Marcar pago"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selected && (
          <LeadModal
            lead={selected}
            onClose={() => setSelected(null)}
            usersMap={usersMap}
            onToggleAdminFlag={toggleAdminFlag}
          />
        )}

        {/* ✅ Modal Fluxo A */}
        {notifyLead && (
          <NotifyLeadModal
            lead={notifyLead}
            usersMap={usersMap}
            onClose={() => setNotifyLead(null)}
            onSent={async ({ leadId, jobId, title }) => {
              // marca como notificado só depois do job criado
              await toggleAdminFlag(leadId, "notified", true, {
                "adminFlags.lastNotifyJobId": jobId || null,
                "adminFlags.lastNotifyTitle": safeStr(title) || null,
                "adminFlags.lastNotifyAt": serverTimestamp(),
              });
            }}
          />
        )}
      </div>
    </BaseLayout>
  );
}

function LeadModal({ lead, onClose, usersMap, onToggleAdminFlag }) {
  const a = lead?.answers || {};
  const u = usersMap?.[safeStr(lead?.userId)] || {};

  const nome = safeStr(a.nome) || safeStr(u.nome) || "—";
  const email = safeStr(a.email) || safeStr(u.email) || "—";
  const tel = safeStr(a.telefone) || safeStr(u.telefone) || "—";

  const adminFlags = lead?.adminFlags || {};
  const notified = adminFlags.notified === true;
  const manualPaid = adminFlags.manualPaid === true;

  const rows = [
    ["Ano", lead?.year],
    ["Status", statusLabel(lead?.status)],
    ["Atualizado", fmtDate(lead?.updatedAt)],
    ["Criado", fmtDate(lead?.createdAt)],
    ["userId", safeStr(lead?.userId) || "—"],
    ["Nome", nome],
    ["E-mail", email],
    ["WhatsApp", tel],
    ["Aceita WhatsApp", a.aceitaWhatsApp === true ? "Sim" : "Não"],
    ["Aceita E-mail", a.aceitaEmail === true ? "Sim" : "Não"],
    ["Notificado (admin)", notified ? "Sim" : "Não"],
    ["Notificado em", fmtDate(adminFlags.notifiedAt)],
    ["Pago manual (admin)", manualPaid ? "Sim" : "Não"],
    ["Pago manual em", fmtDate(adminFlags.manualPaidAt)],
    ["Data nascimento", safeStr(a.dataNascimento) || "—"],
    ["Hora nascimento", safeStr(a.horaNascimento) || "—"],
    ["Cidade", safeStr(a.cidadeNascimento) || "—"],
    ["Estado", safeStr(a.estadoNascimento) || "—"],
    ["País", safeStr(a.paisNascimento) || "—"],
    ["Gênero", safeStr(a.genero) || "—"],
    ["Orientação", safeStr(a.orientacao) || "—"],
    ["Foco do mapa", safeStr(a.focoMapa) || "—"],
    ["Período", safeStr(a.periodoLeitura) || "—"],
    ["Experiência", safeStr(a.experienciaAstro) || "—"],
    ["Status relacionamento", safeStr(a.statusRelacionamento) || "—"],
    ["Objetivo 2026", safeStr(a.objetivo2026) || "—"],
    ["Desafios", safeStr(a.desafios) || "—"],
    ["Tipo que atrai", safeStr(a.tipoPessoaAtrai) || "—"],
    ["Limites", safeStr(a.limites) || "—"],
    ["Medos", safeStr(a.medos) || "—"],
    ["Pontos fortes", safeStr(a.pontosFortes) || "—"],
    ["Preferências", safeStr(a.preferencias) || "—"],
  ];

  return (
    <div className="astro-modal-backdrop" onClick={onClose}>
      <div className="astro-modal" onClick={(e) => e.stopPropagation()}>
        <div className="astro-modal-header">
          <div>
            <h3>Lead — {nome}</h3>
            <p>Detalhes do rascunho do Mapa Astral</p>
          </div>

          <button className="btn btn-ghost" type="button" onClick={onClose}>
            Fechar
          </button>
        </div>

        <div className="astro-modal-body">
          <div className="modal-grid">
            {rows.map(([k, v]) => (
              <div className="modal-item" key={k}>
                <div className="modal-key">{k}</div>
                <div className="modal-val">{safeStr(v) || "—"}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="astro-modal-footer">
          <button
            className={`btn btn-mini ${notified ? "btn-blue" : "btn-ghost"}`}
            type="button"
            onClick={() => onToggleAdminFlag(lead.id, "notified", !notified)}
          >
            {notified ? "Notificado ✓" : "Marcar notificado"}
          </button>

          <button
            className={`btn btn-mini ${manualPaid ? "btn-green" : "btn-ghost"}`}
            type="button"
            onClick={() => onToggleAdminFlag(lead.id, "manualPaid", !manualPaid)}
          >
            {manualPaid ? "Pago ✓" : "Marcar pago"}
          </button>

          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              const txt =
                `Lead Mapa Astral\n` +
                `Nome: ${nome}\n` +
                `Email: ${email}\n` +
                `WhatsApp: ${tel}\n` +
                `Aceita WhatsApp: ${a.aceitaWhatsApp === true ? "Sim" : "Não"}\n` +
                `Aceita Email: ${a.aceitaEmail === true ? "Sim" : "Não"}\n` +
                `Ano: ${lead?.year || "—"}\n` +
                `Status: ${statusLabel(lead?.status)}\n` +
                `Notificado: ${notified ? "Sim" : "Não"}\n` +
                `Pago manual: ${manualPaid ? "Sim" : "Não"}\n` +
                `userId: ${safeStr(lead?.userId) || "—"}\n`;

              navigator.clipboard?.writeText(txt);
            }}
          >
            Copiar contato
          </button>

          <button className="btn btn-ghost" type="button" onClick={onClose}>
            Ok
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ✅ MODAL: Fluxo A (Push)
 * - Cria doc em notifications_jobs
 * - mode=user, targetUserId=lead.userId
 * - data custom pra abrir uma rota do mapa astral (editável)
 */
function NotifyLeadModal({ lead, usersMap, onClose, onSent }) {
  const a = lead?.answers || {};
  const u = usersMap?.[safeStr(lead?.userId)] || {};

  const nome = safeStr(a.nome) || safeStr(u.nome) || "—";
  const targetUserId = safeStr(lead?.userId);

  const [channelId, setChannelId] = useState("fcm_default_channel");
  const [actionType, setActionType] = useState("custom");
  const [customDataText, setCustomDataText] = useState(
    JSON.stringify(
      { type: "custom", route: "/astro-map" }, // ✅ ajuste se sua rota no app for outra
      null,
      2
    )
  );

  // ✅ Template pronto (30% OFF)
  const [title, setTitle] = useState("✨ Seu Mapa Astral está quase pronto!");
  const [message, setMessage] = useState(
    `Oi${nome && nome !== "—" ? `, ${nome}` : ""}! Você deixou seu Mapa Astral em rascunho. Finalize agora e ganhe 30% OFF por tempo limitado. 💗`
  );

  const [loading, setLoading] = useState(false);

  async function sendPushJob() {
    const t = (title || "").trim();
    const m = (message || "").trim();

    if (!targetUserId) {
      alert("Esse lead não tem userId. Não dá pra enviar push.");
      return;
    }

    if (!t || !m) {
      alert("Preencha o título e a mensagem.");
      return;
    }

    // valida JSON se custom
    if (actionType === "custom") {
      try {
        JSON.parse(customDataText || "{}");
      } catch (_) {
        alert("Custom Data (JSON) inválido. Corrija antes de enviar.");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        title: t,
        message: m,
        mode: "user",
        targetUserId,
        channelId: (channelId || "fcm_default_channel").trim(),
        status: "pending",
        createdAt: serverTimestamp(),
        data: buildExtraData(actionType, customDataText),
        // ✅ opcional: referência pra você rastrear de onde saiu
        meta: {
          source: "astro_map_leads",
          leadId: safeStr(lead?.id),
        },
      };

      const ref = await addDoc(collection(db, "notifications_jobs"), payload);

      alert("✅ Job criado! A Cloud Function vai processar e enviar o push.");

      // marca o lead como notificado (somente agora)
      await onSent?.({ leadId: lead.id, jobId: ref?.id, title: t });

      onClose?.();
    } catch (e) {
      console.error(e);
      alert("❌ Erro ao criar job. Veja o console.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="astro-modal-backdrop" onClick={onClose}>
      <div className="astro-modal astro-notify-modal" onClick={(e) => e.stopPropagation()}>
        <div className="astro-modal-header">
          <div>
            <h3>Enviar Push — {nome}</h3>
            <p>Cria um job em <b>notifications_jobs</b> (modo: usuário)</p>
          </div>

          <button className="btn btn-ghost" type="button" onClick={onClose}>
            Fechar
          </button>
        </div>

        <div className="astro-modal-body">
          <div className="notify-grid">
            <div className="notify-row">
              <label className="notify-label">Usuário (UID)</label>
              <div className="notify-value mono">{targetUserId || "—"}</div>
            </div>

            <div className="notify-row">
              <label className="notify-label">Canal (Android)</label>
              <input
                className="notify-input"
                value={channelId}
                onChange={(e) => setChannelId(e.target.value)}
                placeholder="Ex: fcm_default_channel"
              />
            </div>

            <div className="notify-row">
              <label className="notify-label">Ação ao clicar</label>
              <select
                className="notify-input"
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
              >
                <option value="none">Abrir notificações (padrão)</option>
                <option value="custom">Custom (JSON)</option>
              </select>
              <small className="notify-hint">
                💡 Se você já tem uma rota específica no app pro Mapa Astral,
                deixe como <b>custom</b>.
              </small>
            </div>

            {actionType === "custom" && (
              <div className="notify-row">
                <label className="notify-label">Custom Data (JSON)</label>
                <textarea
                  className="notify-textarea mono"
                  value={customDataText}
                  onChange={(e) => setCustomDataText(e.target.value)}
                />
                <small className="notify-hint">
                  Exemplo: <b>{`{"type":"custom","route":"/astro-map"}`}</b>
                </small>
              </div>
            )}

            <div className="notify-row">
              <label className="notify-label">Título</label>
              <input
                className="notify-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Título do push"
              />
            </div>

            <div className="notify-row">
              <label className="notify-label">Mensagem</label>
              <textarea
                className="notify-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Mensagem do push"
              />
            </div>

            <div className="notify-preview">
              <div className="notify-preview-title">Prévia</div>
              <div className="notify-preview-card">
                <div className="notify-preview-h">{title || "Título vazio"}</div>
                <div className="notify-preview-p">{message || "Mensagem vazia"}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="astro-modal-footer">
          <button className="btn btn-ghost" type="button" onClick={onClose}>
            Cancelar
          </button>

          <button
            className="btn btn-primary"
            type="button"
            onClick={sendPushJob}
            disabled={loading}
          >
            {loading ? "Enviando..." : "Enviar Push"}
          </button>
        </div>
      </div>
    </div>
  );
}
